/**
 * ActiveAccountsSection Component
 * 
 * Displays active accounts grouped by account group in accordions with optional drag-and-drop.
 */

import React from "react";
import {
  Box,
  Typography,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  DndContext,
  closestCenter,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Account } from "../../../types";
import { AccountGroupDoc } from "../hooks/useAccountGroups";
import SortableAccountCard, { AccountGroup as SharedAccountGroup } from "./SortableAccountCard";

export interface ActiveAccountsSectionProps {
  accountGroups: AccountGroupDoc[];
  groupedActiveAccounts: Record<string, Account[]>;
  getGroupBalance: (groupName: string) => number;
  getAccountDetailsForDisplay: (account: Account) => Array<{ name: string; value: string | number }>;
  expandedAccountDetails: Set<string>;
  onToggleDetails: (accountId: string) => void;
  onCopyToClipboard: (text: string, feedbackKey?: string) => Promise<void>;
  lastCopiedKey: string | null;
  isMobile: boolean;
  onMenuOpen: (event: React.MouseEvent<HTMLElement>, account: Account) => void;
  formatCurrency: (amount: number, currency: string) => string;
  valuesVisible: boolean;
  defaultCurrency: string;
  enableOrdering: boolean;
  sensors: any; // DndKit sensors
  onAccountDragEnd: (event: DragEndEvent, groupName: string) => Promise<void>;
}

/**
 * Active accounts section component
 */
export default function ActiveAccountsSection({
  accountGroups,
  groupedActiveAccounts,
  getGroupBalance,
  getAccountDetailsForDisplay,
  expandedAccountDetails,
  onToggleDetails,
  onCopyToClipboard,
  lastCopiedKey,
  isMobile,
  onMenuOpen,
  formatCurrency,
  valuesVisible,
  defaultCurrency,
  enableOrdering,
  sensors,
  onAccountDragEnd,
}: ActiveAccountsSectionProps): React.ReactElement {
  return (
    <Stack spacing={0}>
      {accountGroups.map((group) => {
        const groupAccounts = groupedActiveAccounts[group.name];
        if (!groupAccounts || groupAccounts.length === 0) return null;

        const groupBalance = getGroupBalance(group.name);
        return (
          <Accordion
            key={group.id}
            defaultExpanded
            disableGutters
            sx={{
              "& .MuiAccordionSummary-root": { minHeight: { xs: 44, sm: 32 }, py: { xs: 0.5, sm: 0.25 } },
              "&:before": { display: "none" },
              marginTop: 0,
              marginBottom: 1.25,
              "&.Mui-expanded": { marginBottom: 1 },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", pr: 1, gap: 1.5 }}>
                <Typography variant="subtitle2" fontWeight="medium">
                  {group.name} ({groupAccounts.length})
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                    minWidth: 100,
                  }}
                >
                  <Typography 
                    variant="caption" 
                    color="text.secondary" 
                    sx={{ 
                      fontSize: "0.7rem", 
                      lineHeight: 1.2, 
                      mb: 0.15,
                      textTransform: "uppercase",
                      letterSpacing: "0.02em",
                      fontWeight: 500,
                    }}
                  >
                    Balance
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    color={groupBalance >= 0 ? "primary.main" : "error.main"}
                    sx={{ 
                      lineHeight: 1.2, 
                      fontSize: "0.9rem",
                      fontFamily: "inherit",
                    }}
                  >
                    {valuesVisible ? formatCurrency(groupBalance, defaultCurrency) : "•••••"}
                  </Typography>
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 0.75, py: 0, pb: 1.5 }}>
              {enableOrdering ? (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={(e) => onAccountDragEnd(e, group.name)}
                >
                  <SortableContext
                    items={groupAccounts.map((acc) => acc.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <Stack spacing={0.5}>
                      {groupAccounts.map((account) => (
                        <SortableAccountCard
                          key={account.id}
                          account={account}
                          onMenuOpen={onMenuOpen}
                          formatCurrency={formatCurrency}
                          accountGroups={accountGroups as SharedAccountGroup[]}
                          getAccountDetailsForDisplay={getAccountDetailsForDisplay}
                          expandedAccountDetails={expandedAccountDetails}
                          onToggleDetails={onToggleDetails}
                          onCopyToClipboard={onCopyToClipboard}
                          lastCopiedKey={lastCopiedKey}
                          isMobile={isMobile}
                          valuesVisible={valuesVisible}
                          defaultCurrency={defaultCurrency}
                          enableOrdering={true}
                        />
                      ))}
                    </Stack>
                  </SortableContext>
                </DndContext>
              ) : (
                <Stack spacing={0.5}>
                  {groupAccounts.map((account) => (
                    <SortableAccountCard
                      key={account.id}
                      account={account}
                      onMenuOpen={onMenuOpen}
                      formatCurrency={formatCurrency}
                      accountGroups={accountGroups as SharedAccountGroup[]}
                      getAccountDetailsForDisplay={getAccountDetailsForDisplay}
                      expandedAccountDetails={expandedAccountDetails}
                      onToggleDetails={onToggleDetails}
                      onCopyToClipboard={onCopyToClipboard}
                      lastCopiedKey={lastCopiedKey}
                      isMobile={isMobile}
                      valuesVisible={valuesVisible}
                      defaultCurrency={defaultCurrency}
                      enableOrdering={false}
                    />
                  ))}
                </Stack>
              )}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Stack>
  );
}
